import { PermissionEnum } from 'fleet-data-model';
import paths, { deprecatedPaths } from '~/app/paths/paths';
import LogoutContainer from '~/components/containers/LogoutContainer/LogoutContainer';
import UptimeLogoutContainer from '~/domain/uptime/pages/UptimeLogoutContainer';
import UserRoleConstants from '~/constants/user-role-constants';
import * as lazyLoaded from './lazy-loaded-components';
import type { ComponentRoute, RedirectRoute, RouteTypes, SecuredRoute } from './routes.types';

const MANAGER_AND_TECHNICIAN = [UserRoleConstants.MANAGER, UserRoleConstants.TECHNICIAN];

const getRoutes = (): Record<string, RouteTypes> => ({
  acceptUserInvite: {
    path: paths.acceptUserInvite.path,
    Component: lazyLoaded.AcceptInvitePage,
  },

  cookiePolicy: {
    path: paths.cookiePolicy.path,
    Component: lazyLoaded.CookiePolicyPage,
  },

  appRedirect: {
    path: paths.app.path,
    Component: lazyLoaded.AppRedirect,
  },

  login: {
    path: paths.login.path,
    Component: lazyLoaded.OidcStartContainer,
  },

  logout: {
    path: paths.logout.path,
    Component: LogoutContainer,
  },

  operator: {
    path: paths.operator.path,
    Component: lazyLoaded.OperatorLandingPageContainer,
  },

  register: {
    path: paths.register.path,
    Component: lazyLoaded.RegistrationPage,
  },

  salesCompanyLogin: {
    path: paths.salesCompanyLogin.path,
    Component: lazyLoaded.SalescompanyLogin,
  },

  signup: {
    path: paths.signup.path,
    Component: lazyLoaded.SignupPage,
  },

  signupComplete: {
    path: paths.signupComplete.path,
    Component: lazyLoaded.SignupCompletePage,
  },

  signupSuccess: {
    path: paths.signupSuccess.path,
    redirect: (_, search) => paths.login.getPath() + (search ?? ''),
  },

  /* Secured */
  companySettings: {
    path: paths.companySettings.path,
    roles: MANAGER_AND_TECHNICIAN,
    Component: lazyLoaded.CompanySettingsContainer,
    secured: true,
    webviewEnabled: true,
    permission: PermissionEnum.COMPANY_READ,
  },

  changelog: {
    path: paths.changelog.path,
    roles: MANAGER_AND_TECHNICIAN.concat([UserRoleConstants.SALES_COMPANY_USER]),
    secured: true,
    webviewEnabled: true,
    Component: lazyLoaded.ChangeLogContainer,
  },

  dashboard: {
    path: paths.dashboard.path,
    roles: MANAGER_AND_TECHNICIAN,
    secured: true,
    webviewEnabled: true,
    Component: lazyLoaded.DashboardContainer,
  },

  dataExports: {
    path: paths.dataExports.path,
    roles: MANAGER_AND_TECHNICIAN,
    secured: true,
    Component: lazyLoaded.DataExportsContainer,
  },

  dealerAccess: {
    path: paths.dealerAccess.path,
    roles: [UserRoleConstants.MANAGER],
    secured: true,
    permission: PermissionEnum.TRUSTED_DEALER_READ,
    Component: lazyLoaded.DealerAccessContainer,
  },

  gateways: {
    path: paths.gateways.path,
    roles: MANAGER_AND_TECHNICIAN,
    secured: true,
    webviewEnabled: true,
    permission: PermissionEnum.GATEWAY_READ,
    Component: lazyLoaded.GatewaysContainer,
  },

  legal: {
    path: paths.legal.path,
    roles: MANAGER_AND_TECHNICIAN,
    secured: true,
    webviewEnabled: true,
    Component: lazyLoaded.LegalDocumentsContainer,
  },

  roboticInstallation: {
    path: paths.roboticInstallation.path,
    roles: MANAGER_AND_TECHNICIAN,
    secured: true,
    webviewEnabled: true,
    Component: lazyLoaded.RoboticMowerInstallationContainer,
  },

  inventoryOrMachines: {
    path: paths.inventory.path,
    roles: MANAGER_AND_TECHNICIAN,
    secured: true,
    webviewEnabled: true,
    permission: PermissionEnum.ASSET_READ,
    Component: lazyLoaded.InventoryRouteWrapper,
  },

  users: {
    path: paths.users.path,
    roles: [UserRoleConstants.MANAGER],
    secured: true,
    permission: PermissionEnum.USER_READ,
    Component: lazyLoaded.UserManagementContainer,
  },

  usersAndAccess: {
    path: paths.usersAndAccess.path,
    roles: [UserRoleConstants.MANAGER],
    secured: true,
    permission: PermissionEnum.ACCESS_GROUP_READ,
    Component: lazyLoaded.UsersAndAccessContainer,
  },

  /* SalesCompany */
  companies: {
    path: paths.companies.path,
    roles: [UserRoleConstants.SALES_COMPANY_USER],
    Component: lazyLoaded.SalescompanyLogin,
  },

  // Greenkeeper tool
  greenkeeper: {
    path: paths.greenkeeper.path,
    secured: true,
    Component: lazyLoaded.GreenkeeperRoutes,
  },

  inviteRequests: {
    path: paths.inviteRequests.path,
    roles: [UserRoleConstants.SALES_COMPANY_USER],
    secured: true,
    Component: lazyLoaded.ExpediteInviteRequestsContainer,
  },

  enableUsersAndAccess: {
    path: paths.enableUsersAndAccess.path,
    roles: [UserRoleConstants.MANAGER],
    secured: true,
    permission: PermissionEnum.ACCESS_GROUP_READ,
    Component: lazyLoaded.EnableAccessGroupsContainer,
  },

  uptimeCallback: {
    path: paths.uptimeCallback.path,
    Component: lazyLoaded.UptimeCallbackContainer,
  },

  uptimeLogout: {
    path: paths.uptimeLogout.path,
    Component: UptimeLogoutContainer,
  },

  oidcCallback: {
    path: paths.oidcCallback.path,
    Component: lazyLoaded.OidcCallbackContainer,
  },
});

const getDeprecatedRoutes = (): { [k: string]: RedirectRoute } => {
  const deprecatedRoutes: Record<string, RedirectRoute> = {
    _createInvite: {
      path: deprecatedPaths.createInvite.path,
      redirect: (matchParams, search) => paths.signup.getPath(matchParams, search),
    },
  };

  return deprecatedRoutes;
};

interface MappedRoutes {
  securedRoutes: SecuredRoute[];
  securedWebviewRoutes: SecuredRoute[];
  redirectRoutes: RedirectRoute[];
  publicRoutes: ComponentRoute[];
}

export const getMappedRoutes = () => {
  const allRoutes = { ...getDeprecatedRoutes(), ...getRoutes() };
  return Object.values(allRoutes)
    .filter((f) => {
      return !f.pre || f.pre();
    })
    .reduce<MappedRoutes>(
      (acc, route) => {
        if ('secured' in route) {
          acc.securedRoutes.push(route);
          if ('webviewEnabled' in route) {
            acc.securedWebviewRoutes.push(route);
          }
        } else if ('redirect' in route) {
          acc.redirectRoutes.push(route);
        } else {
          acc.publicRoutes.push(route);
        }
        return acc;
      },
      {
        securedWebviewRoutes: [],
        securedRoutes: [],
        redirectRoutes: [],
        publicRoutes: [],
      }
    );
};
